import { KeyRound, Lock, LogOut, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { cn } from "@/utils/cn";

export function UserMenu() {
  const { account, claims, signOut, signIn, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!isAuthenticated || !account) {
    return (
      <button
        type="button"
        className="btn-primary text-xs"
        onClick={() => signIn("/myapps")}
      >
        Sign in
      </button>
    );
  }

  const displayName =
    account.name ||
    (claims?.name as string | undefined) ||
    account.username ||
    "Signed in";
  const upn = account.username || (claims?.preferred_username as string | undefined) || "";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "U";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-0.5 pr-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-white">
          {initials}
        </span>
        <span className="hidden max-w-[140px] truncate sm:inline">{displayName}</span>
      </button>

      <div
        role="menu"
        className={cn(
          "absolute right-0 mt-2 w-72 origin-top-right rounded-tile border border-slate-200 bg-white p-2 shadow-tileHover transition",
          open
            ? "pointer-events-auto scale-100 opacity-100"
            : "pointer-events-none scale-95 opacity-0",
        )}
      >
        <div className="border-b border-slate-100 px-3 pb-3 pt-2">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-500 text-sm font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">
                {displayName}
              </div>
              <div className="truncate text-xs text-slate-500">{upn}</div>
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-col">
          <MenuLink to="/myapps/profile" icon={<UserRound className="h-4 w-4" />}>
            My profile
          </MenuLink>
          <MenuLink
            to="/myapps/change-password"
            icon={<Lock className="h-4 w-4" />}
          >
            Change password
          </MenuLink>
          <MenuLink to="/myapps/tokens" icon={<KeyRound className="h-4 w-4" />}>
            Session &amp; tokens
          </MenuLink>
        </div>

        <div className="mt-2 border-t border-slate-100 pt-2">
          <button
            type="button"
            onClick={() => signOut()}
            className="flex w-full items-center gap-3 rounded px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function MenuLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="flex items-center gap-3 rounded px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
    >
      {icon}
      {children}
    </Link>
  );
}
